import { QueryResult, gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { Button } from '@apollo/orbit';
import { sortBy, sum } from 'lodash';
import React, { useState } from 'react';

import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { Loading } from 'src/components/common/loading/Loading';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import {
  Service_ErroredTraceSummariesQuery,
  Service_ErroredTraceSummariesQueryVariables,
  Service_ErroredTraceSummariesQuery_service_stats_tracePathErrorsRefs,
} from 'src/lib/graphqlTypes/types';

import { bucketSummariesByErrorMessage } from './bucketSummariesByErrorMessage';
import { ErroredTraceItem } from './erroredTraceItem/ErroredTraceItem';

const ErroredTraceSummariesQuery = gql<
  Service_ErroredTraceSummariesQuery,
  Service_ErroredTraceSummariesQueryVariables
>`
  query Service_ErroredTraceSummariesQuery(
    $serviceId: ID!
    $filter: ServiceTracePathErrorsRefsFilter!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $limit: Int!
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(to: $timeTo, from: $timeFrom) {
        tracePathErrorsRefs(filter: $filter, limit: $limit) {
          group: groupBy {
            errorMessage
            queryId
            queryName
            traceStartsAt
            traceId
            traceHttpStatusCode
          }
          metrics {
            errorsCountInPath
          }
        }
      }
    }
  }
`;

export const ErroredTracesList = ({
  fetchMore,
  variables,
  requestsWithErrorsCount,
  data,
  loading,
}: Pick<
  QueryResult<
    Service_ErroredTraceSummariesQuery,
    Service_ErroredTraceSummariesQueryVariables
  >,
  'variables' | 'data' | 'loading' | 'fetchMore'
> & {
  currentLimit?: number;
  requestsWithErrorsCount: number;
}) => {
  const [currentLimit, setCurrentLimit] = useState<number>(10);

  const loadMoreErrors = (nextLimit: number) => {
    fetchMore({
      variables: {
        ...variables,
        limit: nextLimit,
      },
      updateQuery: (_prev, { fetchMoreResult }) => {
        setCurrentLimit(nextLimit);
        return fetchMoreResult;
      },
    });
  };

  const generateErrors = (
    erroredTraceSummaries: Array<
      Pick<
        Service_ErroredTraceSummariesQuery_service_stats_tracePathErrorsRefs,
        'metrics'
      > &
        Service_ErroredTraceSummariesQuery_service_stats_tracePathErrorsRefs['group']
    >,
  ) => {
    const retrievedErrorsCount = sum(
      erroredTraceSummaries.map((summary) => summary.metrics.errorsCountInPath),
    );

    const nextLimit = Math.min(
      retrievedErrorsCount + 100,
      requestsWithErrorsCount,
    );

    const summaryBuckets = bucketSummariesByErrorMessage(erroredTraceSummaries);

    const sortedSummaryBuckets = sortBy(
      summaryBuckets,
      (bucket) => -1 * bucket.summaries.length,
    );
    const traceErrors = sortedSummaryBuckets.map((s, i) => (
      <ErroredTraceItem key={`error-${s.errorMessage || i}`} {...s} />
    ));

    return (
      <div>
        <div className="mx-8 mt-4 px-2">{traceErrors}</div>
        {requestsWithErrorsCount > retrievedErrorsCount &&
        nextLimit > currentLimit ? (
          // The load more link is only rendered when we have received traces from
          // the backend. This ensures that we do not render a load more link before
          // the traces have loaded, which leads to a perceptible flicker when the
          // traces arrive
          <Button
            className="mx-8"
            size="sm"
            onClick={() => loadMoreErrors(nextLimit)}
          >
            Load More Errors
            <span className="ml-2 text-sm italic">
              (
              {(
                requestsWithErrorsCount - retrievedErrorsCount
              ).toLocaleString()}{' '}
              remaining)
            </span>
          </Button>
        ) : requestsWithErrorsCount > retrievedErrorsCount ? (
          <span className="text-sm italic">
            ({requestsWithErrorsCount - retrievedErrorsCount} Errored Traces
            Unavailable)
          </span>
        ) : null}
      </div>
    );
  };

  const erroredTraceSummaries = data?.service?.stats?.tracePathErrorsRefs ?? [];

  if (loading && !erroredTraceSummaries) return <Loading size="xs" />;

  return generateErrors(
    erroredTraceSummaries.map(({ group, metrics }) => ({
      ...group,
      metrics,
    })),
  );
};

export const ErroredTracesListWithData = ({
  serviceId,
  path,
  queryId,
  timeFrom,
  timeTo,
  ...rest
}: {
  serviceId: string;
  path: string | null | undefined;
  queryId: string | undefined;
  requestsWithErrorsCount: number;
  timeFrom: Timestamp['output'];
  timeTo: Timestamp['output'];
}) => {
  return (
    <FilterProvider>
      {({ filter }) => (
        <Query<
          Service_ErroredTraceSummariesQuery,
          Service_ErroredTraceSummariesQueryVariables
        >
          query={ErroredTraceSummariesQuery}
          variables={{
            serviceId,
            timeFrom,
            timeTo,
            limit: 10,
            filter: {
              queryId,
              path: path && Array.isArray(path) ? path.join('.') : path || '',
              ...filter,
            },
          }}
        >
          {(result) => <ErroredTracesList {...result} {...rest} />}
        </Query>
      )}
    </FilterProvider>
  );
};
